import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGroupSummary, type LedgerStat, type ReportGroup } from '@/api/accountsReports';
import type { ExportCell } from '../exportReport';
import { Check, LedgerVouchersView, Loading, PeriodBar, ReportFrame } from './reportParts';
import { amt, num, r2, tdate, td, th, useExcel, useKeyNav, usePeriod, NO_ACTS } from './reportUtils';

/**
 * Tally's Group Summary: groups with their totals; Detailed shows each group's sub-groups and ledgers.
 * A group drills into its own summary (Esc comes back up), a ledger into its vouchers.
 * Columns: [Opening] [Transactions] Closing — each Debit / Credit; Dr and Cr totalled apart.
 */
export interface GroupSummaryProps {
  title: string;
  /** The top level: Tally's primary groups, or these groups by name. */
  roots: 'primaries' | string[];
  defaults: { det?: boolean; op?: boolean; tx?: boolean };
  /** Trial Balance: shows the difference in opening balances when Dr and Cr don't agree. */
  trial?: boolean;
  /** Closing balances only, as at the To date (Balance Sheet drill). */
  asOn?: boolean;
  onBack?: () => void;
}

type Agg = { op: number; dr: number; cr: number; cl: number };
type Line = { kind: 'group' | 'sub' | 'ledger'; label: string; s: Agg; open: () => void; bold: boolean; indent: boolean };

export function GroupSummaryReport({ title, roots, defaults, trial, asOn, onBack }: GroupSummaryProps) {
  const [p] = usePeriod();
  const [det, setDet] = useState(!!defaults.det);
  const [op, setOp] = useState(!!defaults.op);
  const [tx, setTx] = useState(!!defaults.tx);
  const [path, setPath] = useState<number[]>([]);
  const [ledger, setLedger] = useState<number | null>(null);
  const q = useQuery({ queryKey: ['acc-books', 'reports', 'group-summary', p.from, p.to], queryFn: () => getGroupSummary(p) });
  const data = q.data;

  const model = useMemo(() => {
    if (!data) return null;
    const byId = new Map(data.groups.map((g) => [g.id, g]));
    const kids = (id: number | null) => data.groups.filter((g) => g.parentId === id);
    const under = (gid: number, anc: number) => { for (let g = byId.get(gid), i = 0; g && i < 50; g = g.parentId ? byId.get(g.parentId) : undefined, i++) if (g.id === anc) return true; return false; };
    const live = data.ledgers.filter((l) => Math.abs(l.op) > 0.004 || l.dr || l.cr || Math.abs(l.cl) > 0.004);
    const sum = (ls: LedgerStat[]): Agg => ({ op: r2(ls.reduce((s, l) => s + l.op, 0)), dr: r2(ls.reduce((s, l) => s + l.dr, 0)), cr: r2(ls.reduce((s, l) => s + l.cr, 0)), cl: r2(ls.reduce((s, l) => s + l.cl, 0)) });
    const inG = (g: ReportGroup) => live.filter((l) => under(l.groupId, g.id));
    const own = (g: ReportGroup) => live.filter((l) => l.groupId === g.id).sort((a, b) => a.name.localeCompare(b.name));
    const top = roots === 'primaries' ? kids(null) : roots.map((n) => data.groups.find((g) => g.name === n)).filter((g): g is ReportGroup => !!g);
    return { byId, kids, inG, own, sum, top };
  }, [data, roots]);

  // The level shown: the top groups, or inside the group drilled into (one root group opens straight into it).
  const cur = model ? (path.length ? model.byId.get(path[path.length - 1]) : model.top.length === 1 ? model.top[0] : undefined) : undefined;
  const back = path.length ? () => setPath((x) => x.slice(0, -1)) : onBack;
  const lines: Line[] = useMemo(() => {
    if (!model) return [];
    const out: Line[] = [];
    const goGroup = (g: ReportGroup) => () => setPath((x) => [...x, g.id]);
    const goLedger = (l: LedgerStat) => () => setLedger(l.id);
    const level = cur ? [...model.kids(cur.id).filter((g) => model.inG(g).length), ...model.own(cur)] : model.top.filter((g) => model.inG(g).length);
    for (const it of level) {
      if ('nature' in it) {
        out.push({ kind: 'group', label: it.name, s: model.sum(model.inG(it)), open: goGroup(it), bold: true, indent: false });
        if (det) {
          for (const k of model.kids(it.id).filter((g) => model.inG(g).length)) out.push({ kind: 'sub', label: k.name, s: model.sum(model.inG(k)), open: goGroup(k), bold: false, indent: true });
          for (const l of model.own(it)) out.push({ kind: 'ledger', label: l.name, s: l, open: goLedger(l), bold: false, indent: true });
        }
      } else out.push({ kind: 'ledger', label: it.name, s: it, open: goLedger(it), bold: false, indent: false });
    }
    return out;
  }, [model, cur, det]);
  const acts = useMemo(() => lines.map((l) => l.open), [lines]);
  const { row } = useKeyNav(ledger == null ? acts : NO_ACTS, back);
  const excel = useExcel();

  if (ledger != null) return <LedgerVouchersView ledgerId={ledger} from={p.from} to={p.to} onBack={() => setLedger(null)} />;

  const cols: [string, keyof Agg | 'tx'][] = asOn ? [['Closing Balance', 'cl']] : [...(op ? [['Opening Balance', 'op'] as [string, 'op']] : []), ...(tx ? [['Transactions', 'tx'] as [string, 'tx']] : []), ['Closing Balance', 'cl']];
  const pair = (s: Agg, k: keyof Agg | 'tx'): [number, number] => (k === 'tx' ? [s.dr, s.cr] : [Math.max(s[k], 0), Math.max(-s[k], 0)]);
  // Grand total: only the lines at this level that aren't shown inside another (group rows, or ledgers at this level).
  const counted = lines.filter((l) => !l.indent);
  const tot = cols.map(([, k]) => counted.reduce((a, l) => { const [d, c] = pair(l.s, k); return [r2(a[0] + d), r2(a[1] + c)]; }, [0, 0]));
  const clIdx = cols.length - 1;
  const diff = r2(tot[clIdx][0] - tot[clIdx][1]);
  const showDiff = trial && !cur && Math.abs(diff) > 0.01;
  const right = `${cur && cur.name !== title ? `${cur.name} · ` : ''}${asOn ? `as at ${tdate(p.to)}` : `${tdate(p.from)} to ${tdate(p.to)}`}`;

  const doExcel = () => excel(title.replace(/[^A-Za-z]+/g, '_'), title, ['Particulars', ...cols.flatMap(([l]) => [`${l} Dr`, `${l} Cr`])], [
    ...lines.map((l) => [l.indent ? `   ${l.label}` : l.label, ...cols.flatMap(([, k]) => pair(l.s, k).map((x) => x || null))] as ExportCell[]),
    ...(showDiff ? [['Difference in opening balances', ...cols.flatMap((_, i) => (i === clIdx ? [diff < 0 ? -diff : null, diff > 0 ? diff : null] : [null, null]))] as ExportCell[]] : []),
    ['Grand Total', ...tot.flatMap(([d, c], i) => (i === clIdx && showDiff ? [Math.max(d, c), Math.max(d, c)] : [d, c]))],
  ]);

  return (
    <>
      <PeriodBar asOn={asOn} onBack={back} onExcel={data ? doExcel : undefined}>
        <Check label="Detailed" checked={det} onChange={setDet} />
        {!asOn && <Check label="Opening balance" checked={op} onChange={setOp} />}
        {!asOn && <Check label="Transactions" checked={tx} onChange={setTx} />}
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame title={cur && cur.name !== title ? 'Group Summary' : title} right={right}>
          <table className="w-full text-xs" style={{ minWidth: 220 + cols.length * 220 }}>
            <thead>
              <tr className="border-b border-[var(--border-color)]">
                <th rowSpan={2} className={`${th} text-left align-bottom`}>Particulars</th>
                {cols.map(([l]) => <th key={l} colSpan={2} className={`${th} text-center`}>{l}</th>)}
              </tr>
              <tr className="border-b border-[var(--border-color)]">{cols.map(([l]) => [<th key={`${l}d`} className={`${th} text-right`}>Debit</th>, <th key={`${l}c`} className={`${th} text-right`}>Credit</th>])}</tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.kind}-${l.label}-${i}`} {...row(i)}>
                  <td className={`${td} ${l.indent ? 'pl-6 italic' : ''} ${l.bold ? 'font-bold' : l.kind === 'sub' ? 'font-semibold' : ''}`}>{l.label}</td>
                  {cols.map(([, k]) => pair(l.s, k).map((x, j) => <td key={`${k}${j}`} className={`${num} ${l.bold ? 'font-bold' : ''}`}>{amt(x)}</td>))}
                </tr>
              ))}
              {!lines.length && <tr><td className={`${td} py-6 text-center text-[var(--text-muted)]`} colSpan={1 + cols.length * 2}>No balances in this period.</td></tr>}
            </tbody>
            <tfoot>
              {showDiff && (
                <tr className="border-t border-[var(--border-color)] text-warning">
                  <td className={td}>Difference in opening balances</td>
                  {cols.map(([, k], i) => (i === clIdx ? [<td key="d" className={num}>{diff < 0 ? amt(-diff) : ''}</td>, <td key="c" className={num}>{diff > 0 ? amt(diff) : ''}</td>] : [<td key={`${k}d`} />, <td key={`${k}c`} />]))}
                </tr>
              )}
              <tr className="border-t-2 border-[var(--text-primary)] font-extrabold">
                <td className={td}>Grand Total</td>
                {tot.map(([d, c], i) => {
                  const even = i === clIdx && showDiff ? Math.max(d, c) : null;
                  return [<td key={`d${i}`} className={num}>{amt(even ?? d)}</td>, <td key={`c${i}`} className={num}>{amt(even ?? c)}</td>];
                })}
              </tr>
            </tfoot>
          </table>
        </ReportFrame>
      </Loading>
    </>
  );
}
